import axios from "axios";
import {
  asArray,
  normalizeAuthorizationUser,
  normalizeRoleResourcePermission,
  normalizeRoleResourceRow,
} from "./normalize";

const PROGRAMS_AUTH_BASE = `${window.env.API_URL}/v1/programs/authorization`;
const VMS_BASE = `${window.env.API_URL}/api/v1`;

const getToken = () => localStorage.getItem("accessToken");

const getSessionUser = (): Record<string, unknown> => {
  try {
    return JSON.parse(localStorage.getItem("userDetails") || "{}") as Record<string, unknown>;
  } catch {
    return {};
  }
};

const getUserRoleResourceId = (): number => {
  const u = getSessionUser();
  return Number(u.roleResourceId ?? 0);
};

/**
 * Plain JSON + bearer headers. Mirrors the existing user-management calls
 * exactly so RBAC writes don't drift from the proven request shape.
 *
 * NOTE: The principal header (`x-principal-id`) is intentionally omitted
 * here. The working user-management calls never sent it on `/authorization/*`
 * routes, and adding it on RBAC writes (e.g. POST role-resource-permissions)
 * could change how `grantAccess` resolves the caller's context.
 */
const getJsonAuthHeaders = (): Record<string, string> => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const pickArray = <T,>(data: unknown): T[] => asArray<T>(data);

export const RBACService = {
  listVmsRoles: async (params?: { page?: number; limit?: number }) => {
    const response = await axios.get(`${VMS_BASE}/roles`, {
      headers: getJsonAuthHeaders(),
      params: params?.page != null || params?.limit != null ? params : undefined,
    });
    const rows = response.data?.roles?.rows ?? response.data?.data?.roles?.rows;
    return pickArray<Record<string, unknown>>(rows);
  },

  createVmsRole: async (payload: { role: string; description?: string }) => {
    const response = await axios.post(`${VMS_BASE}/roles`, payload, { headers: getJsonAuthHeaders() });
    return response.data?.role ?? response.data;
  },

  updateVmsRole: async (roleId: number, payload: { role?: string; name?: string; description?: string }) => {
    const response = await axios.patch(`${VMS_BASE}/roles/${roleId}`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data?.role ?? response.data;
  },

  deleteVmsRole: async (roleId: number) => {
    await axios.delete(`${VMS_BASE}/roles/${roleId}`, { headers: getJsonAuthHeaders() });
  },

  listPermissions: async () => {
    const response = await axios.get(`${PROGRAMS_AUTH_BASE}/permissions`, {
      headers: getJsonAuthHeaders(),
    });
    return pickArray<Record<string, unknown>>(response.data);
  },

  createPermission: async (payload: { action: string; scope: string }) => {
    const response = await axios.post(`${PROGRAMS_AUTH_BASE}/permissions`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data as Record<string, unknown>;
  },

  /** PUT requires both `action` and `scope` (assigned directly). */
  updatePermission: async (permissionId: number, payload: { action: string; scope: string }) => {
    const response = await axios.put(`${PROGRAMS_AUTH_BASE}/permissions/${permissionId}`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data as Record<string, unknown>;
  },

  deletePermission: async (permissionId: number) => {
    await axios.delete(`${PROGRAMS_AUTH_BASE}/permissions/${permissionId}`, {
      headers: getJsonAuthHeaders(),
    });
  },

  listResources: async () => {
    const response = await axios.get(`${PROGRAMS_AUTH_BASE}/resources`, { headers: getJsonAuthHeaders() });
    return pickArray<Record<string, unknown>>(response.data);
  },

  createResource: async (payload: { resourceName: string; parentRole: number }) => {
    const response = await axios.post(`${PROGRAMS_AUTH_BASE}/resources`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data as Record<string, unknown>;
  },

  updateResource: async (resourceId: number, payload: { resourceName?: string; parentRole?: number }) => {
    const response = await axios.put(`${PROGRAMS_AUTH_BASE}/resources/${resourceId}`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data as Record<string, unknown>;
  },

  deleteResource: async (resourceId: number) => {
    await axios.delete(`${PROGRAMS_AUTH_BASE}/resources/${resourceId}`, { headers: getJsonAuthHeaders() });
  },

  listRoleResources: async () => {
    const response = await axios.get(`${PROGRAMS_AUTH_BASE}/role-resources`, {
      headers: getJsonAuthHeaders(),
    });
    return pickArray<Record<string, unknown>>(response.data).map((row) => normalizeRoleResourceRow(row));
  },

  createRoleResource: async (payload: { role: string; description?: string }) => {
    const response = await axios.post(`${PROGRAMS_AUTH_BASE}/role-resources`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data as Record<string, unknown>;
  },

  updateRoleResource: async (roleResourceId: number, payload: Record<string, unknown>) => {
    const response = await axios.put(`${PROGRAMS_AUTH_BASE}/role-resources/${roleResourceId}`, payload, {
      headers: getJsonAuthHeaders(),
    });
    return response.data as Record<string, unknown>;
  },

  deleteRoleResource: async (roleResourceId: number) => {
    await axios.delete(`${PROGRAMS_AUTH_BASE}/role-resources/${roleResourceId}`, {
      headers: getJsonAuthHeaders(),
    });
  },

  listRoleResourcePermissions: async () => {
    const response = await axios.get(`${PROGRAMS_AUTH_BASE}/role-resource-permissions`, {
      headers: getJsonAuthHeaders(),
    });
    const raw = pickArray<Record<string, unknown>>(response.data);
    return raw.map((row) => normalizeRoleResourcePermission(row));
  },

  createRoleResourcePermissions: async (payload: {
    roleResourceId: number;
    resourceIds: number[];
    permissionIds: number[];
  }) => {
    const response = await axios.post(
      `${PROGRAMS_AUTH_BASE}/role-resource-permissions/${payload.roleResourceId}`,
      { resourceIds: payload.resourceIds, permissionIds: payload.permissionIds },
      { headers: getJsonAuthHeaders() }
    );
    const raw = Array.isArray(response.data)
      ? (response.data as Record<string, unknown>[])
      : asArray<Record<string, unknown>>(response.data);
    return raw.map((row) => normalizeRoleResourcePermission(row as Record<string, unknown>));
  },

  /**
   * PUT body: `roleId` is required and is the grantee role_resource id (stored column name).
   */
  updateRoleResourcePermission: async (
    roleResourcePermissionId: number,
    payload: { roleId: number; resourceId: number; permissionId: number }
  ) => {
    const response = await axios.put(
      `${PROGRAMS_AUTH_BASE}/role-resource-permissions/${roleResourcePermissionId}`,
      payload,
      { headers: getJsonAuthHeaders() }
    );
    return response.data as Record<string, unknown>;
  },

  deleteRoleResourcePermission: async (roleResourcePermissionId: number) => {
    await axios.delete(`${PROGRAMS_AUTH_BASE}/role-resource-permissions/${roleResourcePermissionId}`, {
      headers: getJsonAuthHeaders(),
    });
  },

  getAllotablePermissions: async (resourceId: number) => {
    const response = await axios.get(
      `${PROGRAMS_AUTH_BASE}/role-resource-permissions/${resourceId}/allotable`,
      { headers: getJsonAuthHeaders() }
    );
    return (response.data ?? {}) as Record<string, unknown>;
  },

  /**
   * `GET /authorization/users/:roleResourceId` → `{ message, data: { users, roles } }`.
   */
  listUsersUnderRoleResource: async (roleResourceId?: number) => {
    const rrId = roleResourceId ?? getUserRoleResourceId();
    const response = await axios.get(`${PROGRAMS_AUTH_BASE}/users/${rrId}`, {
      headers: getJsonAuthHeaders(),
    });
    const data = (response.data?.data ?? {}) as Record<string, unknown>;
    const userRows = pickArray<Record<string, unknown>>(data.users);
    const roleRows = pickArray<Record<string, unknown>>(data.roles);
    return {
      users: userRows.map((r) => normalizeAuthorizationUser(r)),
      rolesFromSubTree: roleRows.map((r) => normalizeRoleResourceRow(r)),
    };
  },

  createUser: async (payload: { roleResourceId: number; name: string; email: string; password: string }) => {
    const response = await axios.post(
      `${PROGRAMS_AUTH_BASE}/users/${payload.roleResourceId}`,
      { name: payload.name, email: payload.email, password: payload.password },
      { headers: getJsonAuthHeaders() }
    );
    return response.data as Record<string, unknown>;
  },

  updateUserRoleAssignment: async (payload: { roleResourceId: number; userId: number }) => {
    const currentUserRoleResourceId = getUserRoleResourceId();
    const response = await axios.put(
      `${PROGRAMS_AUTH_BASE}/users/${currentUserRoleResourceId}`,
      { userId: payload.userId, roleResourceId: payload.roleResourceId },
      { headers: getJsonAuthHeaders() }
    );
    return response.data as Record<string, unknown>;
  },

  getGroupedPermissionsForRoleResource: async (roleResourceId: number) => {
    const response = await axios.get(`${PROGRAMS_AUTH_BASE}/users/permissions/${roleResourceId}`, {
      headers: getJsonAuthHeaders(),
    });
    const data = response.data?.data ?? response.data;
    return data as Record<string, unknown>;
  },

  getAdminPortalPermissionsForRoleResource: async (roleResourceId: number) => {
    const response = await axios.get(
      `${PROGRAMS_AUTH_BASE}/users/adminportal/permissions/${roleResourceId}`,
      { headers: getJsonAuthHeaders() }
    );
    const data = response.data?.data ?? response.data;
    return data as Record<string, unknown>;
  },
};

export const RBACContext = {
  getUserRoleResourceId,
};
